import React, { useEffect, useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { colors } from 'src/theme';
import { i18n, useSessionStorage, useCalculationInput, useGetCalculationByBrand, useCreateGeneralOffer, PropertyBrandWithProducts, useGeneralSoContext, showMessage, handleErrorMessage, useLoader, getBrandLogo, BrandEnum, SELECTED_BRAND_KEY, SELECTED_PRODUCT_KEY } from 'src/common';
import { Flex, Text, Button, Card, StepHeader, ContainerComponent, AdditionalFilterCard, TermsAndConditionsModal, LoaderSquare, BrandLogo } from 'src/components';
import { OfferPreviewCard } from 'src/components/cards/offerPreview.card';
import { NavigationService } from 'src/common/utils/navigation.util';
import { AppRoutes } from './_router/app.routes';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet-async';

// Package-specific data
export interface PackageData {
  packageName: string;
  packageCode: string;
  monthlyPrice: number;
  annualPrice: number;
  price: {
    monthlyPrice?: number;
    totalPrice?: number;
  };
  includedCoverages: {
    code: string;
    label: string;
    description?: string;
    variantInfo?: string;
    insuredAmount?: string;
    items?: string[];
    disabled?: boolean;
    isIncluded?: boolean;
  }[];
  calculationId?: string;
  sessionId?: string;
}

export interface AdditionalInfo {
  key: string;
  value: any;
  disabled?: boolean;
  component?: React.ReactNode;
}

const ButtonContainer = styled.div({
  display: 'flex',
  gap: '12px',
  width: '100%',
  marginTop: '16px',
  justifyContent: 'space-between',
  '@media (max-width: 600px)': {
    flexDirection: 'column-reverse',
    gap: '8px',
    alignItems: 'stretch',
  },
  // flexWrap: 'wrap',
});

const EmptyState = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '48px 24px',
  textAlign: 'center',
  gap: '16px',
});

export const OfferPreviewPage: React.FC = () => {
  const { soId, offerId } = useGeneralSoContext();
  const { getValue: getSelectedBrand } = useSessionStorage<{ code: BrandEnum, name: string }>(SELECTED_BRAND_KEY);
  const { getValue: getSelectedProduct, setValue: setProduct } = useSessionStorage<{ brandCode: BrandEnum, code: string, name: string}[]>(SELECTED_PRODUCT_KEY);
  const { setPackageCode, calculationInput, additionalCoverages } = useCalculationInput();
  const { startLoader, stopLoader } = useLoader();

  const [createOffer] = useCreateGeneralOffer();

  const selectedBrandCode = getSelectedBrand()?.code;
  const sessionProd = getSelectedProduct()?.find(prod => prod.brandCode === selectedBrandCode);

  if (!sessionProd) {
    NavigationService.navigate(AppRoutes.WEBSHOP.OFFERS);
  }

  const [selectedProductCode, setSelectedProductCode] = useState<string | null>(sessionProd?.code ?? null);
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  useEffect(() => {
    if (selectedProductCode && selectedBrandCode) {

    };
  }, [selectedProductCode])

  const { calculation, loading: calculationsLoading } = useGetCalculationByBrand(selectedBrandCode!);

  const brandCalc = useMemo(() => {
    if (!calculation || !calculationInput) {
      return null;
    }
    return {
      _id: calculation._id,
      code: calculation.code,
      name: calculation.name,
      products: calculation.products,
    } as PropertyBrandWithProducts;
  }, [calculation, calculationInput]);

  const additionalInfo = useMemo(() => {

    if (!calculation || !calculationInput) {
      return { fields: [], modifiers: []};
    }
    const defaultProduct = brandCalc?.products[0];
    const propertyInfo = calculationInput.propertyInfo;

    const modifiers = defaultProduct?.productTypeSpecific?.modifiers;
    const additionalInfo: AdditionalInfo[] = []

    if (propertyInfo) {
      if (defaultProduct?.calculation?.policyStartDate) {
        additionalInfo.push({
          key: 'policyStartDate',
          value: dayjs(defaultProduct.calculation.policyStartDate).toISOString(),
        });
      }
      if (defaultProduct?.calculation?.policyDuration) {
        additionalInfo.push({
          key: 'policyDuration',
          value: defaultProduct.calculation.policyDuration,
        });
      }
      if (calculation.code === BrandEnum.WIENER ) {
        if (propertyInfo.floor && propertyInfo.floor.length > 0) {
          const floorLabels: Record<string, string> = {
            groundFloor: 'groundFloor',
            floor: 'floor',
            loft: 'loft',
            cellar: 'cellar',
          };
          const floorValue = propertyInfo.floor
            .filter(f => floorLabels[f] || f);
          if (floorValue) {
            additionalInfo.push({
              key: 'floor',
              value: floorValue,
            });
          }
        }
        if (propertyInfo.usage) {
          additionalInfo.push({
            key: 'usage',
            value: propertyInfo.usage,
            disabled: true
          });
        }
        additionalInfo.push({
          key: 'isFlood',
          value: false,
          disabled: true
        })
        additionalInfo.push({
          key: 'expenseExcedes',
          value: false,
          disabled: true
        })
      }
      additionalInfo.push({
        key: 'masonry',
        value: true,
        disabled: true
      })
    }
    return { fields: additionalInfo, modifiers };
  }, [calculation, calculationInput])

  const handleToggleProduct = (productCode: string) => {
    if (brandCalc) {
      const prod = brandCalc?.products.find(product => product.code === productCode) ?? brandCalc.products[0];

      const filteredProds = getSelectedProduct()?.filter(prod => prod.brandCode !== selectedBrandCode);
      setProduct(filteredProds ? [...filteredProds, { brandCode: selectedBrandCode!, code: prod.code, name: prod.name }]
        : [{ brandCode: selectedBrandCode!, code: prod.code, name: prod.name }]
      )
      setSelectedProductCode(prod.code);
    }
  };

  const handleDownloadTerms = () => {
    setTermsModalOpen(true);
  };

  const handleProceedToCheckout = async () => {
    startLoader();
    if (brandCalc) {
      const product = brandCalc.products.find(pack => selectedProductCode === pack.code);
      const selectedCalculation = product?.calculation;
      const selectedAdditionalCoverages = selectedCalculation?.additionalCoverages?.filter(aC =>
        additionalCoverages.find(sAC => sAC.coverageCode === aC.code && brandCalc.code === sAC.brandCode)
      );
      const brandId = brandCalc._id;

      try {
        if (!soId || !product || !product.calculation || !brandId) {
          throw Error;
        };
        const data = await createOffer({
          id: offerId,
          soId,
          product: {
            ...product,
            calculation: {
              ...product.calculation,
              additionalCoverages: selectedAdditionalCoverages ?? []
            }
          },
          brandId,
          additionalCoverages: selectedAdditionalCoverages,
          source: 'property-webshop'
        });

        if (!data) {
          showMessage({
            type: 'error',
            message: handleErrorMessage(undefined),
            duration: 5000,
            fixed: true,
          });
        } else {
          setPackageCode(product.calculation.packageCode);
          NavigationService.navigate(AppRoutes.CHECKOUT.OFFER_CONFIRMATION.replace(':offerId', data.createOffer?._id || ''));
          // NavigationService.navigate(AppRoutes.WEBSHOP.CONTACT);
        }
      } catch (error) {
        showMessage({
          type: 'error',
          message: handleErrorMessage(error),
          duration: 5000,
          fixed: true,
        });
      } finally {
        stopLoader();
      }
    }
  };

  const handleBackToOffers = async () => {
    NavigationService.navigate(AppRoutes.WEBSHOP.OFFERS);
  };

  const isInitialLoad = calculationsLoading && !brandCalc;

  return (
    <ContainerComponent>
      <Helmet>
        <title>Tvoj odabir | Osiguranje imovine - kompare.hr</title>
      </Helmet>
      <StepHeader />
      { isInitialLoad ? (
      <Card padding={8}>
        <Flex vertical gap={8} style={{ minHeight: 200 }}>
          <Flex>
            {selectedBrandCode && <BrandLogo brandCode={selectedBrandCode} src={getBrandLogo(selectedBrandCode)} alt={selectedBrandCode} />}
          </Flex>
          <Flex vertical justify="center" align="center" style={{ flex: 1 }}>
            <LoaderSquare size={80} />
          </Flex>
          <Button
            type="button"
            btnType="primary"
            size="extralarge"
          />
        </Flex>
      </Card>
    ) : !brandCalc ? (
        <LoaderSquare/>
      ) : brandCalc?.products?.length === 0 && calculation ? (
        <EmptyState>
          <Text type="large" color={colors.text1} bold>
            {i18n.t('pages.offerDetails.noOfferSelected')}
          </Text>
          <Text type="base" color={colors.text3}>
            {i18n.t('pages.offerDetails.noOfferDescription')}
          </Text>
          <Button
            text={i18n.t('pages.offerDetails.backToOffers')}
            btnType="primary"
            size="large"
            onClick={handleBackToOffers}
          />
        </EmptyState>
      ) : (
        <Flex vertical gap={24} >
          { additionalInfo.fields && additionalInfo.modifiers && (
            <AdditionalFilterCard
              additionalInfo={additionalInfo.fields}
              modifiers={additionalInfo.modifiers}
            />
          )}
          <OfferPreviewCard
            brandCalc={brandCalc}
            onTogglePackage={handleToggleProduct}
            toggledProductCode={selectedProductCode}
            onDownloadTerms={handleDownloadTerms}
            calculationsLoading={calculationsLoading}
          />
        </Flex>
      )}

      <ButtonContainer>
        <Button
          text={i18n.t('pages.offerDetails.backToOffers')}
          btnType="outlineBlack"
          size="large"
          onClick={handleBackToOffers}
        />
        <Button
          text={i18n.t('pages.offerDetails.proceedToCheckout')}
          btnType="primary"
          size="large"
          onClick={handleProceedToCheckout}
          style={{ flex: '1 0 0' }}
        />
      </ButtonContainer>

      <TermsAndConditionsModal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
      />
    </ContainerComponent>
  );
};

export default OfferPreviewPage;
